import { Sources } from '@/components/FeeBreakdownTable'
import { Card } from '@/components/ui/Card'
import { DataTable, type DataColumn } from '@/components/ui/DataTable'
import { ErrorState } from '@/components/ui/ErrorState'
import { TableSkeleton } from '@/components/ui/Skeleton'
import { moneyText } from '@/lib/finance'
import { fmtInt } from '@/lib/format'
import { pctOf, subDecimal, sumDecimal } from '@/lib/num'
import { channelLabel } from '@/lib/paymentLabels'

/** One `fees_by_channel` line of GET /accounting/summary (src/core/accounting/service.py
 *  ChannelFees.as_dict). */
export interface ChannelFeeRow {
  channel: string
  label: string
  method: string
  payments: number
  gross_rub: string
  fee_rub: string
  net_rub: string
  effective_pct: string
  /** fee in ₽ per source: provider_actual | provider_quote | rule_estimate */
  fee_by_source: Record<string, string>
}

/** One `revenue_by_channel` line (the paid payments of the period, no fees). */
export interface ChannelRevenueRow {
  channel: string
  label: string
  method: string
  payments: number
  gross_rub: string
}

/** On a phone a row is the channel and its fee (the page is about fees) on the first line, the
 *  fee's sources under it, then the other figures as label–value pairs (WP-25): «Платежей»,
 *  «Выручка, ₽», «Чистыми, ₽», «Эффективно, %» — before, the list dropped them and a phone saw
 *  the fee alone. */
const COLUMNS: DataColumn<ChannelFeeRow>[] = [
  { id: 'channel', header: 'Канал оплаты', type: 'name', cell: (r) => r.label || channelLabel(r.channel), mobile: 'title' },
  { id: 'payments', header: 'Платежей', type: 'number', className: 'text-ink-2', cell: (r) => fmtInt(r.payments), mobile: 'field' },
  { id: 'gross', header: 'Выручка, ₽', type: 'money', cell: (r) => moneyText(r.gross_rub), mobile: 'field' },
  { id: 'fee', header: 'Комиссия, ₽', unit: '₽', type: 'money', className: 'text-ink-2', cell: (r) => moneyText(r.fee_rub), mobile: 'amount' },
  { id: 'net', header: 'Чистыми, ₽', type: 'money', cell: (r) => moneyText(r.net_rub), mobile: 'field' },
  { id: 'pct', header: 'Эффективно, %', type: 'percent', className: 'text-ink-2', cell: (r) => moneyText(r.effective_pct), mobile: 'field' },
  {
    id: 'sources',
    header: 'Источник цифр',
    type: 'status',
    cell: (r) => <Sources sources={r.fee_by_source} align="start" />,
    mobile: 'status',
  },
]

/**
 * Бухгалтерия → revenue and payment-provider fees per payment channel: the method and the
 * gateway that issued the invoice (СБП через PayGateCore or IDMPay, Heleket, Stars). The channel
 * comes from payments.gateway, so IDMPay fees — recorded under provider «sbp» — get their own
 * line here. Totals are exact decimal sums, the table's totals row (DataTable). Presentational;
 * every state in Storybook.
 */
export function ChannelBreakdownTable({
  items,
  error = null,
  onRetry,
}: {
  items: ChannelFeeRow[] | undefined
  /** the failed request (an ApiError) or its message */
  error?: unknown
  onRetry?: () => void
}) {
  if (error) {
    return (
      <Card>
        <ErrorState title="Разбивка по каналам не загрузилась" error={error} onRetry={onRetry} />
      </Card>
    )
  }
  if (items === undefined) {
    return (
      <Card className="p-0">
        <TableSkeleton cols={7} rows={4} />
      </Card>
    )
  }
  if (items.length === 0) {
    return (
      <Card>
        <p className="text-body text-ink-3">Оплат за период нет. Выберите другой период или канал.</p>
      </Card>
    )
  }
  // MONEY: exact decimal sums (no float)
  const gross = sumDecimal(items.map((r) => r.gross_rub))
  const fee = sumDecimal(items.map((r) => r.fee_rub))
  const totals =
    items.length > 1
      ? {
          label: 'Итого по каналам',
          cells: {
            payments: fmtInt(items.reduce((a, r) => a + r.payments, 0)),
            gross: moneyText(gross),
            fee: moneyText(fee),
            net: moneyText(subDecimal(gross, fee)),
            pct: moneyText(pctOf(fee, gross)),
          },
        }
      : undefined
  return (
    <DataTable
      label="Выручка и комиссии по каналам оплаты"
      rows={items}
      columns={COLUMNS}
      getRowId={(r) => r.channel}
      totals={totals}
    />
  )
}
